import { ChangeDetectorRef, Directive, EmbeddedViewRef, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Destroyable, takeUntilDestroy } from '../lifecycle';

type ObservablesContainerType<Type extends Record<string, unknown>> = {
    [Property in keyof Type]: Observable<Type[Property]>;
};

export type CombineAsyncContext<TData> = {
    $implicit: TData;
    ngCombineAsync: TData;
} & TData;

@Directive({
    selector: '[ngCombineAsync]',
    standalone: true
})
export class CombineAsyncDirective<TData extends Record<string, unknown>> extends Destroyable {
    private view?: EmbeddedViewRef<CombineAsyncContext<TData>>;

    private observablesContainerSubject: Subject<ObservablesContainerType<TData>> = new Subject<ObservablesContainerType<TData>>();

    public static ngTemplateContextGuard<TData extends Record<string, unknown>>(
        directive: CombineAsyncDirective<TData>,
        context: unknown
    ): context is CombineAsyncContext<TData> {
        return true;
    }

    /**
     *
     * @param observablesContainer - object that all its properties are observables.

     * @returns object that contains an object for each property in the input param with its latest emit.
     *
     *  * @example
     *
     *      <ng-container *ngCombineAsync="vm$ as vm">
     *
     *     </ng-container>
     *
     *  input:
     *         interface ObservablesContainer {
     *             firstObs: Observable<number>;
     *             secondObs: Observable<string>;
     *         }
     *
     *  output:
     *         {
     *            firstObs: number;
     *            secondObs: string;
     *        }
     *
     *  */
    @Input() set ngCombineAsync(observablesContainer: ObservablesContainerType<TData>) {
        this.observablesContainerSubject.next(observablesContainer);
    }

    constructor(
        private viewContainer: ViewContainerRef,
        private template: TemplateRef<CombineAsyncContext<TData>>,
        private cd: ChangeDetectorRef
    ) {
        super();
        this.initObservablesContainer();
    }

    private initObservablesContainer(): void {
        this.observablesContainerSubject
            .pipe(
                switchMap((observablesContainer) => combineLatest(observablesContainer)),
                takeUntilDestroy(this)
            )
            .subscribe((data) => {
                this.updateView(data);
            });
    }

    private updateView(data: TData): void {
        const context: CombineAsyncContext<TData> = this.createContext(data);

        if (!this.view) {
            this.view = this.viewContainer.createEmbeddedView(this.template, context);
        } else {
            this.view.context = context;
        }

        this.cd.markForCheck();
    }

    private createContext(data: TData): CombineAsyncContext<TData> {
        return {
            $implicit: data,
            ngCombineAsync: data,
            ...data
        };
    }
}
